/**
 * SPEC-72 Phase 2 — web half of the WKWebView cookie flush.
 *
 * Phase 1 (native, ShellView.swift — CLOSED) injects a document-start
 * userscript defining `window.__eagleCookieFlush`, which posts to the
 * "eagleCookieFlush" script-message handler and triggers a device_token-
 * scoped WKHTTPCookieStore flush. That bridge is dormant until the page
 * calls it — this module is the call.
 *
 * Fire-and-forget, best-effort: by the time a caller fires this, the login
 * response (and its Set-Cookie) is already processed. Persistence is the
 * goal, never correctness — a throwing or absent bridge must not disturb
 * login. No imports: dependency-free by contract (instructions §1).
 */

/**
 * Local, NARROW declaration of the native-injected bridges. Deliberately NOT
 * a `declare global` — nfc.ts owns this file-family's global Window block
 * and is out of scope (instructions §3). The fields are optional: absent in
 * plain browsers, Android Chrome, and stale bundles — all silent no-ops.
 * `__eagleCookieClear` is the Phase B2 logout twin (posts to the native
 * "eagleCookieClear" handler).
 */
interface CookieFlushWindow {
  __eagleCookieFlush?: () => void;
  __eagleCookieClear?: () => void;
}

/**
 * Fire the native cookie flush once. Optional-chained + try/catch:
 * - absent `__eagleCookieFlush` → silent no-op (plain browser path);
 * - throwing userscript → swallowed (login must never break on it);
 * - `win` parameter honored so tests can pass a fake window — the real
 *   global is never touched.
 *
 * Audit BUG #5: the optional `win` is resolved INSIDE the try. A default
 * parameter (`= window`) evaluates before the body, so a missing global
 * threw a ReferenceError that escaped this function into the caller's
 * catch — surfacing "window is not defined" after a successful login and
 * skipping `onLoggedIn`. Now a missing global is swallowed like every
 * other failure of this best-effort path.
 */
export function fireCookieFlush(win?: Window): void {
  try {
    ((win ?? window) as Window & CookieFlushWindow).__eagleCookieFlush?.();
  } catch {
    // Contract (SPEC-72 Phase 2 §1): swallow everything. The flush is
    // best-effort persistence behind an already-processed login response.
  }
}

/**
 * SPEC-72 Phase B2 — the logout twin of fireCookieFlush. Posts to the native
 * "eagleCookieClear" handler; the shell answers with CookieVault.clearAll(),
 * wiping the Keychain-persisted auth cookies so an EXPLICIT logout stays
 * logged out across process death (without this, the Phase B1 vault would
 * resurrect the session at next launch).
 *
 * Deliberately NOT called from api.ts's kick path: a server-side revocation
 * (office Reset Device, replay, token rotation) must NOT clear the vault —
 * the tokens are already dead server-side, and clearing from the kick path
 * could wipe a session another surface still holds. Stale vault values simply
 * fail server validation and hit the login gate. This asymmetry is the
 * designed behavior (SPEC-72 Phase B instructions §B2; do not "fix").
 *
 * Same contract as fireCookieFlush: fire-and-forget, best-effort, optional
 * `win` resolved INSIDE the try (audit BUG #5), silent no-op when the bridge
 * is absent (plain browser / Android Chrome / stale bundles).
 */
export function fireCookieClear(win?: Window): void {
  try {
    ((win ?? window) as Window & CookieFlushWindow).__eagleCookieClear?.();
  } catch {
    // Contract: swallow everything — logout must never break on the bridge.
  }
}
